import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDoc, query, orderBy, Timestamp, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

// Renamed from ProjectResource
export interface ProposalResource {
    quantity: number;
    typeId: string;
    technologyId: string;
    seniority: string;
}

// Renamed from Project
export interface Proposal {
    id?: string;
    name: string; // This will be the proposal name/title
    clientId: string; // Client this proposal is for (renamed from client for clarity)
    duration: string;
    expectedClosing?: string;
    proposalDate?: string;
    heatLevel: 'frozen' | 'cold' | 'cool' | 'warm' | 'hot' | 'burning';
    status: 'negotiating' | 'won' | 'lost' | 'cancelled';
    resources: ProposalResource[];
    manager: string;
    notes?: string;
    proposalUrl?: string; // Path for proposal PDFs will likely be proposals/ in Firebase Storage
    createdAt?: string;
    updatedAt?: string;
}

// Renamed from useProjects
export function useProposals() {
    const [proposals, setProposals] = useState<Proposal[]>([]); // Renamed from projects
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        if (!user) {
            setProposals([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const proposalsQuery = query(collection(db, 'proposals'), orderBy('createdAt', 'desc'));

        const unsubscribe: Unsubscribe = onSnapshot(proposalsQuery,
            (querySnapshot) => {
                const proposalsList = querySnapshot.docs.map(doc => {
                    const proposalData = doc.data() as Proposal;
                    return {
                        id: doc.id,
                        ...proposalData
                    } as Proposal;
                });
                setProposals(proposalsList);
                setLoading(false);
                setError(null); // Clear error on successful fetch
            },
            (err) => {
                setError('Erro ao carregar propostas em tempo real.');
                setLoading(false);
            }
        );

        // Cleanup listener on component unmount
        return () => {
            unsubscribe();
        };
    }, [user]); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    // Renamed from addProject
    const addProposal = async (proposal: Omit<Proposal, 'id' | 'createdAt' | 'status'>) => {
        try {
            const now = Timestamp.now();
            const docRef = await addDoc(collection(db, 'proposals'), {
                ...proposal,
                createdAt: now,
                updatedAt: now
            });
            return { ...proposal, id: docRef.id, createdAt: now.toDate().toISOString(), updatedAt: now.toDate().toISOString() };
        } catch (err) {
            throw new Error('Erro ao adicionar proposta');
        }
    };

    // Renamed from updateProject
    const updateProposal = async (id: string, updates: Partial<Proposal>) => {
        try {
            const now = Timestamp.now();
            await updateDoc(doc(db, 'proposals', id), {
                ...updates,
                updatedAt: now
            });
        } catch (err) {
            throw new Error('Erro ao atualizar proposta');
        }
    };

    const deleteProposal = async (id: string) => {
        try {
            const proposalRef = doc(db, 'proposals', id);
            const proposalDoc = await getDoc(proposalRef);

            if (proposalDoc.exists()) {
                const proposalData = proposalDoc.data() as Proposal;
                const proposalDocRef = doc(db, 'proposals', id);

                if (proposalData.proposalUrl) {
                    try {
                        const storage = getStorage();
                        const decodedUrl = decodeURIComponent(proposalData.proposalUrl);
                        // Ensure regex matches the actual structure of your proposal URLs
                        const filePathRegex = /\/o\/proposals%2F(.+)\?alt=media/i;
                        // Or a more generic one if paths vary: /\/o\/(.+)\?alt=media/;
                        const match = decodedUrl.match(filePathRegex);

                        if (match && match[1]) {
                            // The filePath for proposal PDFs should be like 'proposals/filename.pdf'
                            const filePathInStorage = `proposals/${match[1]}`;
                            const fileRef = ref(storage, filePathInStorage);
                            await deleteObject(fileRef);
                        } else {
                            console.warn('Não foi possível extrair o caminho do arquivo da proposalUrl para exclusão no Storage. URL:', proposalData.proposalUrl, 'Regex used:', filePathRegex.toString());
                        }
                    } catch (storageError) {
                        console.error('Erro ao excluir arquivo da proposta do Storage:', storageError);
                    }
                }

                await deleteDoc(proposalDocRef);
            } else {
                console.warn('Documento da proposta não encontrado para exclusão de arquivo.');
            }
        } catch (err) {
            throw new Error('Erro geral ao excluir proposta');
        }
    };

    return { proposals, loading, error, addProposal, updateProposal, deleteProposal };
} 